/**
 * IPLocationsTab - Display organization signup locations for admin
 * Shows where organizations signed up from geographically
 */
import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Globe,
  MapPin,
  Clock,
  Building2,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Users,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IPLocationsTab = () => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stats, setStats] = useState({ 
    total_organizations: 0, 
    with_location_data: 0, 
    unique_countries: 0,
    unique_cities: 0 
  });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });

  const fetchOrganizations = async (skip = 0) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ip-tracking/admin/organizations`, {
        params: { skip, limit: pagination.limit }
      });
      
      if (response.data.success) {
        setOrganizations(response.data.organizations);
        setStats(response.data.stats);
        setPagination(prev => ({ ...prev, skip, total: response.data.total }));
      }
    } catch (error) {
      console.error("Error fetching organizations:", error);
      toast.error("Failed to load organization location data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const filteredOrganizations = organizations.filter(org => {
    const term = searchTerm.toLowerCase();
    const loc = org.signupLocation || {};
    return (
      org.name?.toLowerCase().includes(term) ||
      org.ownerEmail?.toLowerCase().includes(term) ||
      loc.country?.toLowerCase().includes(term) ||
      loc.city?.toLowerCase().includes(term) ||
      loc.ip?.toLowerCase().includes(term)
    );
  });

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return "N/A";
      return format(parseISO(dateStr), "MMM d, yyyy");
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-green-100 text-green-800",
      trial: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-100 text-red-800",
      expired: "bg-gray-100 text-gray-800"
    };
    return (
      <Badge className={variants[status] || "bg-gray-100 text-gray-800"}>
        {status || "unknown"}
      </Badge>
    );
  };

  const getPlanBadge = (plan) => {
    const variants = {
      basic: "bg-amber-100 text-amber-800",
      premium: "bg-blue-100 text-blue-800",
      deluxe: "bg-purple-100 text-purple-800",
      enterprise: "bg-emerald-100 text-emerald-800",
      signature: "bg-indigo-100 text-indigo-800"
    };
    return (
      <Badge className={variants[plan?.toLowerCase()] || "bg-gray-100 text-gray-800"}>
        {plan || "trial"}
      </Badge>
    );
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode) return "🌍";
    try {
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt(0));
      return String.fromCodePoint(...codePoints);
    } catch {
      return "🌍";
    }
  };

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Organization IP Locations Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1a365d; border-bottom: 2px solid #3182ce; padding-bottom: 10px; }
          .stats { display: flex; gap: 30px; margin-bottom: 20px; }
          .stat { background: #f7fafc; padding: 15px; border-radius: 8px; }
          .stat-label { font-size: 12px; color: #718096; }
          .stat-value { font-size: 24px; font-weight: bold; color: #2d3748; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #edf2f7; padding: 12px 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #cbd5e0; }
          td { padding: 10px 8px; border-bottom: 1px solid #e2e8f0; }
          tr:nth-child(even) { background: #f7fafc; }
          .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; }
          .badge-active { background: #c6f6d5; color: #276749; }
          .badge-trial { background: #bee3f8; color: #2b6cb0; }
          .badge-expired { background: #fed7d7; color: #c53030; }
          .timestamp { color: #718096; font-size: 12px; }
          .print-date { text-align: right; color: #718096; font-size: 12px; margin-bottom: 20px; }
        </style>
      </head>
      <body>
        <h1>Organization IP Locations Report</h1>
        <p class="print-date">Generated: ${new Date().toLocaleString()}</p>
        <div class="stats">
          <div class="stat"><div class="stat-label">Total Organizations</div><div class="stat-value">${stats.total_organizations}</div></div>
          <div class="stat"><div class="stat-label">With Location Data</div><div class="stat-value">${stats.with_location_data}</div></div>
          <div class="stat"><div class="stat-label">Countries</div><div class="stat-value">${stats.unique_countries}</div></div>
          <div class="stat"><div class="stat-label">Cities</div><div class="stat-value">${stats.unique_cities}</div></div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Owner Email</th>
              <th>Location</th>
              <th>IP Address</th>
              <th>Status</th>
              <th>Plan</th>
              <th>Signed Up</th>
            </tr>
          </thead>
          <tbody>
            ${filteredOrganizations.map(org => {
              const loc = org.signupLocation || {};
              const statusClass = org.status === 'active' ? 'badge-active' : org.status === 'trial' ? 'badge-trial' : 'badge-expired';
              return `
                <tr>
                  <td>${org.name || 'Unnamed'}</td>
                  <td>${org.ownerEmail || 'N/A'}</td>
                  <td>${loc.city ? `${loc.city}, ${loc.country || ''}` : 'No location data'}</td>
                  <td>${loc.ip || '-'}</td>
                  <td><span class="badge ${statusClass}">${org.status || 'unknown'}</span></td>
                  <td>${org.plan || 'trial'}</td>
                  <td class="timestamp">${formatDate(org.createdAt || loc.captured_at)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border border-line shadow-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-xl">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-ink-muted">Total Organizations</p>
                <p className="text-2xl font-bold text-ink-main">{stats.total_organizations}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border border-line shadow-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl">
                <MapPin className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-ink-muted">With Location Data</p>
                <p className="text-2xl font-bold text-ink-main">{stats.with_location_data}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border border-line shadow-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-xl">
                <Globe className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-ink-muted">Countries</p>
                <p className="text-2xl font-bold text-ink-main">{stats.unique_countries}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-line shadow-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-amber-100 rounded-xl">
                <Users className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-ink-muted">Cities</p>
                <p className="text-2xl font-bold text-ink-main">{stats.unique_cities}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Actions */}
      <Card className="bg-white border border-line shadow-card">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-ink-muted" />
              <Input
                placeholder="Search by organization, email, country, city, or IP..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-line"
              />
            </div>
            <Button
              onClick={() => fetchOrganizations(0)}
              variant="outline"
              className="border-brand-primary text-brand-primary hover:bg-brand-primary/5"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={handlePrint}
              variant="outline"
              className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
              disabled={loading || filteredOrganizations.length === 0}
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Organizations Table */}
      <Card className="bg-white border border-line shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-ink-main">
            <MapPin className="w-6 h-6 text-brand-primary" />
            Organization Signup Locations
            <Badge variant="outline" className="ml-2">
              {pagination.total} total
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-ink-muted">Loading organization data...</p>
            </div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="text-center py-12">
              <Globe className="w-12 h-12 text-brand-primary/30 mx-auto mb-4" />
              <p className="text-ink-muted">
                {searchTerm ? "No organizations match your search" : "No organizations found"}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-surface-bg">
                      <TableHead className="font-semibold text-ink-main">Organization</TableHead>
                      <TableHead className="font-semibold text-ink-main">Owner Email</TableHead>
                      <TableHead className="font-semibold text-ink-main">Location</TableHead>
                      <TableHead className="font-semibold text-ink-main">IP Address</TableHead>
                      <TableHead className="font-semibold text-ink-main">Status</TableHead>
                      <TableHead className="font-semibold text-ink-main">Plan</TableHead>
                      <TableHead className="font-semibold text-ink-main">Signed Up</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrganizations.map((org, idx) => {
                      const loc = org.signupLocation || {};
                      return (
                        <TableRow key={org.id || idx} className="hover:bg-surface-bg/50">
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-ink-muted" />
                              <span className="font-medium text-ink-main">
                                {org.name || "Unnamed"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-ink-main">
                              {org.ownerEmail || "N/A"}
                            </span>
                          </TableCell>
                          <TableCell>
                            {loc.country ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{getCountryFlag(loc.country_code)}</span>
                                <div>
                                  <div className="font-medium text-ink-main">
                                    {loc.city || "Unknown"}{loc.region ? `, ${loc.region}` : ""}
                                  </div>
                                  <div className="text-sm text-ink-muted">
                                    {loc.country}
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-ink-muted italic">No location data</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {loc.ip ? (
                              <code className="px-2 py-1 bg-gray-100 rounded text-sm">
                                {loc.ip}
                              </code>
                            ) : (
                              <span className="text-ink-muted">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(org.status)}
                          </TableCell>
                          <TableCell>
                            {getPlanBadge(org.plan)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm text-ink-muted">
                              <Clock className="w-4 h-4" />
                              {formatDate(org.createdAt || loc.captured_at)}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-line">
                <p className="text-sm text-ink-muted">
                  Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchOrganizations(Math.max(0, pagination.skip - pagination.limit))}
                    disabled={pagination.skip === 0 || loading}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fetchOrganizations(pagination.skip + pagination.limit)}
                    disabled={pagination.skip + pagination.limit >= pagination.total || loading}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IPLocationsTab;
